import { Check, Video } from 'lucide-react';
import { PUBS, money, type AdSize, type PubChoice } from '@generated/components/pricing';

interface Props {
  sizes: AdSize[]; loading: boolean; pub: PubChoice | null; sizeId: string | null;
  onPub: (p: PubChoice) => void; onSize: (id: string) => void; videoOnly: boolean; onVideoOnly: () => void;
}

function PagePreview({ w, h }: { w: number; h: number }) {
  const scale = 4.4;
  return (
    <div className="relative mx-auto bg-background ring-1 ring-foreground/30" style={{ width: 10 * scale, height: 15 * scale }} aria-hidden>
      <div className="absolute left-0 top-0 bg-primary/80" style={{ width: Math.min(w, 10) * scale, height: Math.min(h, 15) * scale }} />
    </div>
  );
}

export function StepPublication({ sizes, loading, pub, sizeId, onPub, onSize, videoOnly, onVideoOnly }: Props) {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">Choose your publication</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {PUBS.map((p) => {
            const on = !videoOnly && pub === p.id;
            return (
              <button key={p.id} onClick={() => onPub(p.id)}
                className={`relative border p-5 text-left transition-colors ${on ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-card hover:border-foreground/50'}`}>
                {on && <Check className="absolute right-3 top-3 h-4 w-4 text-primary" />}
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{p.id === 'bundle' ? 'Both papers' : 'Circulation 5,000'}</div>
                <div className="mt-2 font-[family-name:var(--font-heading)] text-xl font-semibold">{p.name}</div>
                <p className="mt-2 text-sm text-muted-foreground">{p.blurb}</p>
              </button>
            );
          })}
        </div>
        <button onClick={onVideoOnly} className={`relative mt-3 flex w-full items-start gap-3 border p-5 text-left ${videoOnly ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-dashed border-foreground/40 bg-card hover:border-foreground/60'}`}>
          {videoOnly && <Check className="absolute right-3 top-3 h-4 w-4 text-primary" />}
          <Video className="mt-1 h-5 w-5 shrink-0 text-primary" />
          <div>
            <div className="font-[family-name:var(--font-heading)] text-xl font-semibold">Brennan's Report only — no print ad</div>
            <p className="mt-1 text-sm text-muted-foreground">Sponsor our monthly 5-minute video show on Instagram, YouTube and Facebook. $1,995 per episode; 6 episodes for the price of 5.</p>
          </div>
        </button>
      </section>
      {!videoOnly && <section>
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">Select an ad size</h2>
        <p className="mt-1 text-sm text-muted-foreground">Shaded area shows how the ad sits on a 10" × 15" page.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {loading && Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-48 animate-pulse bg-muted" />)}
          {!loading && sizes.map((s) => {
            const on = sizeId === s.id;
            return (
              <button key={s.id} onClick={() => onSize(s.id)}
                className={`flex flex-col items-center gap-3 border p-4 text-center ${on ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-card hover:border-foreground/50'}`}>
                <PagePreview w={s.widthInches ?? 1} h={s.heightInches ?? 1} />
                <div>
                  <div className="text-sm font-semibold leading-tight">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.widthInches}" × {s.heightInches}"</div>
                  <div className="mt-1 font-[family-name:var(--font-heading)] text-lg">{money(s.pricePerIssue ?? 0)}<span className="text-xs text-muted-foreground">/mo</span></div>
                  {s.color && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.color}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </section>}
    </div>
  );
}
